import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@heroui/react";
import { R1DataTable } from "../components/R1DataTable";
import R1FilterBar from "../components/R1FilterBar";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import {
  buildFundScopeParams,
  buildR1DownloadUrl,
  fundSelectionKey,
  r1Api,
  type R1Fund,
  type R1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

function buildPeFundsFilteredUrl(selection: R1FundSelection): string {
  const params = buildFundScopeParams(selection);
  return buildR1DownloadUrl("r1_pe_funds", params);
}

const textCell = (key: string): ColumnDef<R1Fund> => ({
  accessorKey: key,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return String(v);
  },
});

const boolCell = (key: string, header: string): ColumnDef<R1Fund> => ({
  accessorKey: key,
  header,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return v ? "Yes" : "No";
  },
});

const pctCell = (key: string, header: string): ColumnDef<R1Fund> => ({
  accessorKey: key,
  header,
  meta: { className: "text-right" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return (n * 100).toFixed(1) + "%";
  },
});

const columns: ColumnDef<R1Fund>[] = [
  { ...textCell("fund_name"), header: "Fund Name", size: 200 },
  {
    ...textCell("name_of_general_partner"),
    header: "General Partner",
    size: 180,
  },
  { ...textCell("manager"), header: "Manager", size: 160 },
  { ...textCell("legal_form_fund"), header: "Legal Form", size: 120 },
  {
    ...textCell("fund_investment_state"),
    header: "Investment Status",
    size: 140,
  },
  { ...textCell("fund_domicile"), header: "Fund Domicile", size: 120 },
  { ...textCell("team_location_country"), header: "Team Location", size: 120 },
  {
    ...boolCell("fund_with_connection_to_germany", "Connection to Germany"),
    size: 80,
  } as ColumnDef<R1Fund>,
  { ...textCell("fund_currency"), header: "Currency", size: 80 },
  { ...textCell("commitment_date"), header: "Commitment Date", size: 120 },
  { ...textCell("signature_date"), header: "Signature Date", size: 120 },
  { ...textCell("liquidation_date"), header: "Liquidation Date", size: 120 },
  {
    accessorKey: "vintage_year",
    header: "Vintage Year",
    size: 90,
    cell: ({ getValue }) => getValue() ?? "",
  },
  { ...textCell("first_closing_date"), header: "First Closing", size: 110 },
  { ...textCell("final_closing_date"), header: "Final Closing", size: 110 },
  {
    ...textCell("first_investment_date_fund"),
    header: "First Investment",
    size: 120,
  },
  {
    ...textCell("end_of_investment_period"),
    header: "End of Inv. Period",
    size: 130,
  },
  { ...textCell("planned_end_date"), header: "Planned End", size: 110 },
  { ...textCell("generation"), header: "Generation", size: 90 },
  { ...textCell("sector_focus"), header: "Sector Focus", size: 140 },
  { ...textCell("strategy_focus"), header: "Strategy Focus", size: 140 },
  { ...textCell("geographic_focus"), header: "Geographic Focus", size: 140 },
  { ...textCell("asset_type_fund"), header: "Asset Type", size: 110 },
  { ...textCell("product_type"), header: "Product Type", size: 110 },
  { ...textCell("lei_code_fund"), header: "LEI Code", size: 160 },
  {
    ...textCell("in_which_closing_joined"),
    header: "Closing Joined",
    size: 120,
  },
  {
    ...pctCell("pct_of_private_investors", "% Private Investors"),
    size: 110,
  } as ColumnDef<R1Fund>,
  { ...textCell("gp_name"), header: "GP", size: 160 },
  { ...textCell("management_company_id"), header: "GP-ID", size: 80 },
  {
    ...boolCell("trr_required", "TRR required"),
    size: 100,
  } as ColumnDef<R1Fund>,
  { ...textCell("deadline_nb_days"), header: "Reporting deadline", size: 130 },
  {
    ...boolCell("first_production", "Included in 1st prod"),
    size: 120,
  } as ColumnDef<R1Fund>,
];

export default function PeFundsView() {
  const { selectedFundIds, fundSelection } = useR1DashboardContext();

  const { data, isLoading } = useQuery({
    queryKey: ["r1", "pe-funds-view", fundSelectionKey(fundSelection)],
    queryFn: () => r1Api.getFunds({ selection: fundSelection }),
    staleTime: 60_000,
    enabled: fundSelection.mode === "all" || selectedFundIds.length > 0,
  });

  const filteredRows = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <R1FilterBar hidePeriod />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">PE Funds</h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {filteredRows.length.toLocaleString()} funds
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <R1DataTable<R1Fund, unknown>
              table_name="r1-pe-funds"
              columns={columns}
              data={filteredRows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["fund_name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildPeFundsFilteredUrl(fundSelection)}
                filename={`r1_pe_funds_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
